import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ViewStateOptions } from '@common/data-models/navigation/viewstate-options';
import { ViewStateLayouts } from '@common/enums/viewstate-layouts';
import { selectI18N } from '@common/store/i18n/selectors/i18n.selectors';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { SingleFeedViewStateRecord } from '@src/suite/common/data-models/records/single-feed-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { StoryViewStateRecord } from '@src/suite/common/data-models/records/story-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { GroupKey } from '@src/suite/common/data-models/search-results/group-key';
import { ResultGroup } from '@src/suite/common/data-models/search-results/result-group';
import { TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import {
    CARD_HEIGHT,
    CARD_LAST_ITEM_HEIGHT,
    CONDENSED_CARD_HEIGHT,
    CONDENSED_FIRST_ITEM_HEADER_HEIGHT,
    CONDENSED_LAST_ITEM_HEIGHT,
    CUSTOM_FIELDS_HEIGHT,
    GROUP_HEADER_HEIGHT,
} from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { selectNodeExpandState } from '@src/suite/common/store/list/list.selectors';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { selectAssignmentRecordsMap, selectPublicationChannelsMap, selectPublicationListsMap, selectPublications, selectTopicRecordsMap } from '@src/suite/common/store/records/records.selectors';
import moment from 'moment';

import { CastState } from './cast.models';
import { CANDIDATE_ID_PREFIX, getIsNodeExpandable, getNodeChildren, getNodeLevel, getRecordsTreeLikeData, NEW_SECTION_ID_PREFIX, nodeTransformer, SECTIONS_SEQUENCE_FIELD } from './cast.utils';

export const selectCurrentCastViewState = createSelector(selectCurrentViewState, state => (state instanceof PublicationViewStateRecord ? state : null));

export const selectCastLayoutOptions = createSelector(selectI18N, i18n => {
    const ret: ViewStateOptions = new ViewStateOptions();
    ret.Default = ViewStateLayouts.CONDENSE;
    ret.Options = [
        { id: ViewStateLayouts.CAST_CARD, title: i18n?.PSTWebViewCardLabel },
        { id: ViewStateLayouts.CONDENSE, title: i18n?.PSTWebViewCondensedLabel },
    ];
    return ret;
});

export const selectCastGroupingOptions = createSelector(selectI18N, i18n => {
    const ret: ViewStateOptions = new ViewStateOptions();
    ret.Default = PSTFieldNames.PSTChannel;
    ret.Options = [{ id: PSTFieldNames.PSTChannel, title: i18n?.PSTWebGroupByChannelLabel }];
    return ret;
});

export const selectViewDateRange = createSelector(selectCurrentCastViewState, selectCurrentApplication, (vs, application) => {
    if (application === 'cast') {
        const time = (<PublicationViewStateRecord>vs)?.publicationTime;
        const firstDay: DateTime = new DateTime();
        const lastDay: DateTime = new DateTime();
        let fromDate: Date = null;
        let toDate: Date = null;

        if (time?.date) {
            fromDate = moment(time.date).startOf('day').toDate();
        } else {
            fromDate = moment().startOf('day').toDate();
        }
        toDate = moment(fromDate).endOf('day').toDate();

        firstDay.set(fromDate);
        lastDay.set(toDate);

        return { firstDate: firstDay, lastDate: lastDay };
    }
    return null;
});

export const selectCurrentPublicationChannel = createSelector(selectCurrentCastViewState, selectPublicationChannelsMap, (vs, publicationChannels) => {
    if (vs instanceof PublicationViewStateRecord) {
        return publicationChannels.get(vs.publicationChannel?.Guid);
    }
    return null;
});

const selectCastState = createFeatureSelector<CastState>('cast');

export const selectCurrentPublicationListGuid = createSelector(selectCastState, state => {
    return state.publicationListGuid;
});

export const selectCurrentPublicationList = createSelector(selectCurrentPublicationListGuid, selectPublicationListsMap, (publicationListGuid, publicationLists) => {
    if (publicationListGuid) {
        return publicationLists.get(publicationListGuid) || null;
    }
    return null;
});

export const selectCurrentPublicationListRecords = createSelector(selectCurrentPublicationList, selectPublications, (publicationList, publications) => {
    return (publicationList?.Sequence || []).map(guid => publications.get(guid));
});

// export const selectCurrentPublicationListLinkedRecordGuids = createSelector(selectCurrentPublicationListRecords, list => {
//     return new Map((list || [])?.map(r => [r.Guid, r?.Relationships?.getGuid(0)]));
// });

// export const selectCurrentPublicationListLinkedRecords = createSelector(
//     selectCurrentPublicationListLinkedRecordGuids,
//     selectTopicRecordsMap,
//     selectAssignmentRecordsMap,
//     (guidsMap, topicRecords, assignmentRecords) => {
//         const ret: Map<string, StorytellingRecord> = new Map(
//             [...(guidsMap.keys() || [])]
//                 .map(guid => {
//                     const recordGuid = guidsMap.get(guid);
//                     return [guid, topicRecords.get(recordGuid) || assignmentRecords.get(recordGuid)];
//                 })
//                 .filter(([_, record]) => !!record) as Array<[string, StorytellingRecord]>
//         );
//         return ret;
//     }
// );

export const selectCurrentPublicationListLinkedRecords = createSelector(
    selectCurrentPublicationListRecords,
    selectTopicRecordsMap,
    selectAssignmentRecordsMap,
    (publications, topicRecords, assignmentRecords) => {
        const ret: Map<string, StorytellingRecord> = new Map(
            (publications || [])
                .map(publication => {
                    if (!publication) {
                        return null;
                    }
                    const linkedRecordGuid = publication?.Relationships?.getGuid(0);
                    const recordGuid = linkedRecordGuid ? topicRecords.get(linkedRecordGuid) || assignmentRecords.get(linkedRecordGuid) : null;
                    if (recordGuid) {
                        publication.linkedRecords = [recordGuid];
                    }
                    return [publication.Guid, publication];
                })
                .filter(record => !!record) as Array<[string, StorytellingRecord]>
        );
        return ret;
    }
);

export const selectCurrentPublicationListLinkedRecordToPublicationMap = createSelector(selectCurrentPublicationListLinkedRecords, publications => {
    const ret: Map<string, string> = new Map(
        [...(publications?.values() || [])]
            .map(publication => {
                if (!publication) {
                    return null;
                }
                const linkedRecord = publication.linkedRecords[0];
                if (!linkedRecord) {
                    return null;
                }
                return [linkedRecord.Guid, publication.Guid];
            })
            .filter(record => !!record) as Array<[string, string]>
    );
    return ret;
});

export const selectCurrentPublicationListSections = createSelector(selectCurrentPublicationList, selectCurrentCastViewState, (list, vs) => {
    let publicationSequenceCollection: {
        [section: string]: Array<string>;
    } = null;
    if (list?.PublicationSequenceCollection != null) {
        try {
            publicationSequenceCollection = JSON.parse(list?.PublicationSequenceCollection);
        } catch (e) {
            console.error(e);
        }
    }
    if (publicationSequenceCollection == null) {
        publicationSequenceCollection = {};
        const sections = (vs as PublicationViewStateRecord)?.publicationChannel?.PSTPublicationChannelSections?.split('|')
            .map((item: string) => item.trim())
            .filter(Boolean);
        if (sections?.length > 0) {
            for (const section of sections) {
                publicationSequenceCollection[section] = [];
            }
        }
    }

    let sections = {};
    const sequence = publicationSequenceCollection[SECTIONS_SEQUENCE_FIELD]?.filter(s => s !== SECTIONS_SEQUENCE_FIELD);
    if (sequence) {
        for (const guid of sequence) {
            sections[guid] = publicationSequenceCollection[guid];
        }
        sections[SECTIONS_SEQUENCE_FIELD] = sequence;
    } else {
        // Fallback if we don't have a sequence, BUT we should always have a sequence
        sections = publicationSequenceCollection;
        sections[SECTIONS_SEQUENCE_FIELD] = Object.keys(publicationSequenceCollection).filter(s => s !== SECTIONS_SEQUENCE_FIELD);
    }

    return sections;
});

export const selectCurrentPublicationListSectionKeys = createSelector(selectCurrentPublicationListSections, publicationSequenceCollection => {
    // return Object.keys(publicationSequenceCollection || {})?.filter(s => s !== SECTIONS_SEQUENCE_FIELD) || [];
    return publicationSequenceCollection?.[SECTIONS_SEQUENCE_FIELD] || [];
});

export const selectCurrentPublicationListSectionsWithLinkedRecordGuids = createSelector(
    selectCurrentPublicationListSections,
    selectCurrentPublicationListLinkedRecords,
    (publicationSequenceCollection, linkedRecords) => {
        const newSections: { [section: string]: Array<string> } = {};
        for (const section of Object.keys(publicationSequenceCollection || {})) {
            if (section !== SECTIONS_SEQUENCE_FIELD) {
                newSections[section] = [];
                for (const guid of publicationSequenceCollection[section]) {
                    const recordGuid = linkedRecords.get(guid)?.linkedRecords?.[0]?.Guid;
                    if (recordGuid) {
                        newSections[section].push(recordGuid);
                    }
                }
            } else {
                newSections[section] = publicationSequenceCollection[section];
            }
        }

        return newSections;
    }
);

export const selectHasMoreThanOneSection = createSelector(selectCurrentPublicationListSectionKeys, sectionKeys => {
    return sectionKeys?.length > 1;
});

export const selectNewSectionPosition = createSelector(selectCastState, state => {
    return state.newSectionPosition;
});

export const selectTreeDataSourceControlPair = createSelector(
    selectCurrentPublicationChannel,
    selectCurrentPublicationListLinkedRecords,
    selectNodeExpandState,
    selectCurrentPublicationListSections,
    selectNewSectionPosition,
    selectI18N,
    (currentPubChannel, linkedRecords, nodesExpandState, sections, newSectionPosition, languageSettings) => {
        const records: ResultGroup<StorytellingRecord> = new ResultGroup<StorytellingRecord>();
        if (!currentPubChannel) return {};

        const sectionKeys = [...sections[SECTIONS_SEQUENCE_FIELD]];

        if (newSectionPosition != null && newSectionPosition > -1) {
            const position = Math.min(newSectionPosition, sectionKeys.length - 1);
            sectionKeys.splice(position + 1, 0, `${NEW_SECTION_ID_PREFIX}${languageSettings.PSTWebNewSectionLabel}`);
        }

        if (sectionKeys.length > 0) {
            for (const index in sectionKeys) {
                const key = sectionKeys[index];
                const groupKey = new GroupKey(key, key);
                if (sections?.[key]?.length > 0) {
                    let sectionRecordAdded = false;
                    for (const guid of sections[key]) {
                        const record = linkedRecords.get(guid);
                        if (record) {
                            sectionRecordAdded = true;
                            records.addToGroups([groupKey], record);
                        }
                    }
                    if (!sectionRecordAdded) {
                        records.keys.push(groupKey);
                    }
                } else {
                    records.keys.push(groupKey);
                }
            }
        }

        const treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>(getNodeLevel, getIsNodeExpandable, {
            trackBy: node => node.uniqueId,
        });
        const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);
        // Populates our flattened data into the tree control
        const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);

        treeDataSource.data = getRecordsTreeLikeData(records as ResultGroup<StorytellingRecord>);

        treeControl.expandAll();
        treeControl.dataNodes.forEach(node => {
            const state = nodesExpandState[node.id];
            if (state != null) {
                if (state === false) {
                    treeControl.collapseDescendants(node);
                } else {
                    treeControl.expandDescendants(node);
                }
            }
        });
        return { treeDataSource, treeControl };
    }
);
export const selectTreeDataSource = createSelector(selectTreeDataSourceControlPair, pair => pair?.treeDataSource);
export const selectTreeControl = createSelector(selectTreeDataSourceControlPair, pair => pair?.treeControl);

/*
export const selectCurrentNormalCardItemHeights = createSelector(selectTreeControl, selectCurrentViewState, (treeControl, vs) => {
    if (!vs || !treeControl?.dataNodes) return [];
    // const objtype = getObjectTypeByViewStateType(vs?.viewStateType);
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                case 1: {
                    if (item.object.Type === ObjectTypes.TOPIC) {
                        if (item.nodeLastInGroup) {
                            return 152;
                        }
                        return 144;
                    }
                    if (item.object.Type === ObjectTypes.SCRIPT) {
                        if (item.nodeLastInGroup) {
                            return CARD_LAST_ITEM_HEIGHT;
                        }
                        return CARD_HEIGHT;
                    }
                    return 32;
                }
                default: {
                    if (item.nodeFirstInGroup && item.nodeLastInGroup) {
                        if (item.object.Type === ObjectTypes.ASSIGNMENT) {
                            return CARD_LAST_ITEM_HEIGHT + CHILD_CARD_HEADER_HEIGHT;
                        }
                    }
                    if (item.nodeLastInGroup) {
                        return CARD_LAST_ITEM_HEIGHT;
                    }
                    if (item.nodeFirstInGroup) {
                        if (item.object.Type === ObjectTypes.ASSIGNMENT) {
                            return CARD_HEIGHT + CHILD_CARD_HEADER_HEIGHT;
                        }
                    }
                    return CARD_HEIGHT;
                }
            }
        })
        .filter(height => height > 0);
    return heights;
});
export const selectCurrentCondesedItemHeights = createSelector(selectTreeControl, treeControl => {
    if (!treeControl) return [];
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                default: {
                    if (item.nodeLastInGroup && item.nodeFirstInGroup) {
                        return CONDENSED_LAST_ITEM_HEIGHT + CONDENSED_FIRST_ITEM_HEADER_HEIGHT;
                    }
                    if (item.nodeLastInGroup) {
                        return CONDENSED_LAST_ITEM_HEIGHT;
                    }
                    if (item.nodeFirstInGroup) {
                        return CONDENSED_CARD_HEIGHT + CONDENSED_FIRST_ITEM_HEADER_HEIGHT;
                    }
                    return CONDENSED_CARD_HEIGHT;
                }
            }
        });
    return heights;
});

export const selectCurrentCardItemCustomFieldsHeights = createSelector(selectTreeControl, selectCurrentViewState, (treeControl, vs) => {
    if (!vs || !treeControl?.dataNodes) return [];
    // const objtype = getObjectTypeByViewStateType(vs?.viewStateType);
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                default: {
                    const objtype = item.object.Type;
                    if (item.nodeFirstInGroup && item.nodeLastInGroup) {
                        if (objtype === ObjectTypes.ASSIGNMENT || objtype === ObjectTypes.SCRIPT) {
                            return CARD_LAST_ITEM_HEIGHT + CHILD_CARD_HEADER_HEIGHT + CUSTOM_FIELDS_HEIGHT;
                        }
                    }
                    if (item.nodeLastInGroup) {
                        return CARD_LAST_ITEM_HEIGHT + CUSTOM_FIELDS_HEIGHT;
                    }
                    if (item.nodeFirstInGroup) {
                        if (objtype === ObjectTypes.ASSIGNMENT || objtype === ObjectTypes.SCRIPT) {
                            return CARD_HEIGHT + CHILD_CARD_HEADER_HEIGHT + CUSTOM_FIELDS_HEIGHT;
                        }
                    }
                    return CARD_HEIGHT + CUSTOM_FIELDS_HEIGHT;
                }
            }
        })
        .filter(height => height > 0);
    return heights;
});

export const selectCurrentCardItemHeights = createSelector(
    selectCurrentNormalCardItemHeights,
    selectCurrentCondesedItemHeights,
    selectCurrentCardItemCustomFieldsHeights,
    selectCurrentViewState,
    (normal, condensed, customFields, currentVs) => {
        if (!currentVs) return [];
        if (currentVs?.viewStateLayout?.Value === ViewStateLayouts.CONDENSE) {
            return condensed;
        }

        if (currentVs.viewStateType === ViewStateTypesEnum.PUBLICATION || currentVs.viewStateType === ViewStateTypesEnum.TOPIC) {
            if ((currentVs as SingleViewStateRecord)?.viewStateCustomCardItems?.length > 0) {
                return customFields;
            }
        }

        return normal;
    }
);
*/

// TODO: extract numbers to constants
export const selectCurrentNormalCardItemHeights = createSelector(selectTreeControl, selectCurrentCastViewState, (treeControl, vs) => {
    if (!vs || !treeControl?.dataNodes) return [];
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            // console.log(item.linkedRecordType);
            // switch (item.linkedRecordType) {
            //     case ObjectTypes.ASSIGNMENT: {
            //         switch (item.level) {
            //             case 0:
            //                 return GROUP_HEADER_HEIGHT;
            //             case 1:
            //                 return 144;
            //             default: {
            //                 if (item.nodeLastInGroup) {
            //                     return 152;
            //                 }
            //                 return 144;
            //             }
            //         }
            //     }
            //     default: {
            //         switch (item.level) {
            //             case 0:
            //                 return GROUP_HEADER_HEIGHT;
            //             default: {
            //                 if (item.nodeLastInGroup) {
            //                     return 152;
            //                 }
            //                 return 144;
            //             }
            //         }
            //     }
            // }
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                case 1: {
                    if (item.nodeLastInGroup) {
                        return CARD_LAST_ITEM_HEIGHT;
                    }
                    return CARD_HEIGHT;
                }
                default: {
                    if (item.nodeLastInGroup) {
                        return CARD_LAST_ITEM_HEIGHT;
                    }
                    return CARD_HEIGHT;
                }
            }
        })
        .filter(height => height > 0);
    return heights;
});

// TODO: extract numbers to constants
export const selectCurrentCondesedItemHeights = createSelector(selectTreeControl, treeControl => {
    if (!treeControl) return [];
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            // switch (item?.linkedRecordType) {
            //     default: {
            //         switch (item.level) {
            //             case 0:
            //                 return 32;
            //             default: {
            //                 if (item.nodeLastInGroup) {
            //                     return 56;
            //                 }
            //                 if (item.nodeFirstInGroup) {
            //                     return 72;
            //                 }
            //                 return 48;
            //             }
            //         }
            //     }
            // }

            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                default: {
                    if (item.nodeLastInGroup) {
                        return CONDENSED_LAST_ITEM_HEIGHT;
                    }
                    if (item.nodeFirstInGroup) {
                        return CONDENSED_CARD_HEIGHT + CONDENSED_FIRST_ITEM_HEADER_HEIGHT;
                    }
                    return CONDENSED_CARD_HEIGHT;
                }
            }
        });
    return heights;
});

export const selectTileItemSize = createSelector(() => {
    return 146;
});

// TODO: extract numbers to constants
export const selectCurrentCardItemCustomFieldsHeights = createSelector(selectTreeControl, selectCurrentCastViewState, (treeControl, vs) => {
    if (!vs || !treeControl?.dataNodes) return [];
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            // switch (item?.linkedRecordType) {
            //     case ObjectTypes.ASSIGNMENT: {
            //         switch (item.level) {
            //             case 0:
            //                 return 32;
            //             case 1: {
            //                 if (item.isNewItems) {
            //                     if (item.nodeLastInGroup) {
            //                         return 152;
            //                     }
            //                     return 144;
            //                 }
            //                 return 32;
            //             }
            //             default: {
            //                 if (item.nodeLastInGroup) {
            //                     return 152;
            //                 }
            //                 return 144;
            //             }
            //         }
            //     }
            //     default: {
            //         switch (item.level) {
            //             case 0:
            //                 return 32;
            //             default: {
            //                 if (item.nodeLastInGroup) {
            //                     return 188;
            //                 }
            //                 return 180;
            //             }
            //         }
            //     }
            // }

            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                case 1: {
                    // if (item.isNewItems) {
                    //     if (item.nodeLastInGroup) {
                    //         return 152;
                    //     }
                    //     return 144;
                    // }
                    // return 32;
                    switch (item.linkedRecordType) {
                        case ObjectTypes.ASSIGNMENT: {
                            if (item.nodeLastInGroup) {
                                return CARD_LAST_ITEM_HEIGHT;
                            }
                            return CARD_HEIGHT;
                        }
                        default: {
                            if (item.nodeLastInGroup) {
                                return CARD_LAST_ITEM_HEIGHT + CUSTOM_FIELDS_HEIGHT;
                            }
                            return CARD_HEIGHT + CUSTOM_FIELDS_HEIGHT;
                        }
                    }
                }
                default: {
                    if (item.nodeLastInGroup) {
                        return CARD_LAST_ITEM_HEIGHT;
                    }
                    return CARD_HEIGHT;
                }
            }
        })
        .filter(height => height > 0);
    return heights;
});

export const selectCurrentCardItemHeights = createSelector(
    selectCurrentNormalCardItemHeights,
    selectCurrentCondesedItemHeights,
    selectCurrentCardItemCustomFieldsHeights,
    selectCurrentViewState,
    (normal, condensed, customFields, currentVs) => {
        if (!currentVs) return [];
        if (currentVs?.viewStateLayout?.Value === ViewStateLayouts.CONDENSE) {
            return condensed;
        }

        if (currentVs.viewStateType === ViewStateTypesEnum.PUBLICATION || currentVs.viewStateType === ViewStateTypesEnum.TOPIC) {
            if ((currentVs as SingleViewStateRecord)?.viewStateCustomCardItems?.length > 0) {
                return customFields;
            }
        }

        return normal;
    }
);

export const selectSavedViewStates = createSelector(selectCastState, selectCurrentCastViewState, (state, currentViewState) => {
    return state.storedViewStates?.filter(vs => {
        if (currentViewState && currentViewState instanceof PublicationViewStateRecord && vs) {
            if (currentViewState.viewStateShowTopics && (vs.viewStateType === ViewStateTypesEnum.TOPIC || vs.viewStateType === ViewStateTypesEnum.ASSIGNMENT)) {
                return true;
            }

            if (!currentViewState.viewStateShowTopics && currentViewState.viewStateShowAssignments && vs.viewStateType === ViewStateTypesEnum.ASSIGNMENT) {
                return true;
            }
        }
        return false;
    });
});

export const selectSelectedSavedViewState = createSelector(selectCastState, selectSavedViewStates, (state, savedViewStates) => {
    return savedViewStates?.find(vs => vs.guid === state.currentViewStateGuid);
});

export const selectCastViewStateCustomCardItems = createSelector(selectSelectedSavedViewState, (vs: ViewStateRecord) => {
    if (
        vs instanceof TopicViewStateRecord ||
        vs instanceof StoryViewStateRecord ||
        vs instanceof SingleFeedViewStateRecord ||
        vs instanceof SingleViewStateRecord ||
        vs instanceof PublicationViewStateRecord
    )
        return vs.viewStateCustomCardItems;
    return [];
});

export const selectSideRecords = createSelector(
    selectCastState,
    selectTopicRecordsMap,
    selectAssignmentRecordsMap,
    selectCurrentCastViewState,
    selectCurrentPublicationListLinkedRecordToPublicationMap,
    (state, topicRecords, assignmentRecords, currentViewState, currentPublicationListLinkedRecordToPublicationMap) => {
        return (state.sideRecordGuids || [])
            .map(guid => {
                let record = null;
                if (currentViewState && currentViewState instanceof PublicationViewStateRecord) {
                    if (currentViewState.viewStateRemoveAddedRecords && currentPublicationListLinkedRecordToPublicationMap?.has(guid)) {
                        return null;
                    }
                    if (currentViewState.viewStateShowTopics) {
                        record = topicRecords.get(guid);
                    }
                    if (!record && currentViewState.viewStateShowAssignments) {
                        record = assignmentRecords.get(guid);
                    }
                }
                return record;
            })
            .filter(record => !!record);
    }
);

export const selectSideRecordsTreeDataPair = createSelector(selectSideRecords, records => {
    const treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>(getNodeLevel, getIsNodeExpandable, {
        trackBy: node => node.uniqueId,
    });
    const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);
    // Populates our flattened data into the tree control
    const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);
    // treeDataSource.data = getRecordsTreeLikeData(records as ResultGroup<StorytellingRecord>);
    treeDataSource.data = records.map((record, index) => {
        return {
            id: record?.Guid,
            uniqueId: `${CANDIDATE_ID_PREFIX}${record.Guid}`,
            children: [],
            object: record,
            nodeFirstInGroup: index === 0,
            nodeLastInGroup: index === records.length - 1,
        };
    });

    return { treeDataSource, treeControl };
});

export const selectSideRecordsTreeDataSource = createSelector(selectSideRecordsTreeDataPair, pair => pair?.treeDataSource);
export const selectSideRecordsTreeControl = createSelector(selectSideRecordsTreeDataPair, pair => pair?.treeControl);

export const selectIsDragStarted = createSelector(selectCastState, state => !!state.draggingStarted);
export const selectDragRecordGuid = createSelector(selectCastState, state => state.dragRecordGuid);

export const selectProcessing = createSelector(selectCastState, state => state.processing);
export const selectListLoading = createSelector(selectCastState, state => state.listLoading);

export const selectSideRecordsLoading = createSelector(selectCastState, state => state.sideRecordsLoading);
